import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../utils/api';

// ─── tiny local helpers ────────────────────────────────────────────────────
const Bdg = ({ color = 'gray', children }) => {
  const cls = {
    green: 'bg-green-900/40 text-green-300',
    red: 'bg-red-900/40 text-red-300',
    yellow: 'bg-yellow-900/40 text-yellow-300',
    blue: 'bg-blue-900/40 text-blue-300',
    teal: 'bg-teal-900/40 text-teal-300',
    gray: 'bg-gray-800 text-gray-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls[color] || cls.gray}`}>
      {children}
    </span>
  );
};
const Btn = ({ onClick, loading, disabled, size = 'md', color, children, to, variant }) => {
  const base = 'rounded px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const variantCls =
    color === 'green' ? 'bg-green-900/40 hover:bg-green-900/60 text-green-300' :
    color === 'red'   ? 'bg-red-900/40 hover:bg-red-900/60 text-red-300' :
    variant === 'light' ? 'bg-blue-900/40 hover:bg-blue-900/60 text-blue-300' :
    variant === 'default' ? 'border border-gray-600 hover:bg-gray-800 text-white' :
    'bg-gray-700 hover:bg-gray-600 text-white';
  const sm = size === 'xs' ? 'px-2 py-1 text-xs' : '';
  if (to) return <Link to={to} className={`${base} ${variantCls} ${sm}`}>{children}</Link>;
  return (
    <button className={`${base} ${variantCls} ${sm}`} onClick={onClick} disabled={disabled || loading}>
      {loading ? '…' : children}
    </button>
  );
};

const DEFAULT_READINESS_QUESTIONS = [
  {
    question_key: 'team_size',
    question_text: 'How many team members currently execute this workflow?',
    category: 'operations',
    weight_multiplier: 1,
    max_score: 5,
    is_active: true,
  },
  {
    question_key: 'manual_hours_per_person_weekly',
    question_text: 'How many manual hours per person are spent weekly on this workflow?',
    category: 'operations',
    weight_multiplier: 1.5,
    max_score: 5,
    is_active: true,
  },
  {
    question_key: 'hourly_cost',
    question_text: 'What is the average hourly labor cost for the team handling this process?',
    category: 'financial',
    weight_multiplier: 1,
    max_score: 5,
    is_active: true,
  },
  {
    question_key: 'tool_count',
    question_text: 'How many tools are currently used end-to-end for this process?',
    category: 'operations',
    weight_multiplier: 1,
    max_score: 5,
    is_active: true,
  },
  {
    question_key: 'data_centralization',
    question_text: 'How centralized is your process data today?',
    category: 'data',
    weight_multiplier: 2,
    max_score: 5,
    is_active: true,
  },
  {
    question_key: 'change_readiness',
    question_text: 'How ready is your team to adopt process automation changes?',
    category: 'change',
    weight_multiplier: 2,
    max_score: 5,
    is_active: true,
  },
  {
    question_key: 'compliance_risk',
    question_text: 'How high is compliance risk in the current manual workflow?',
    category: 'governance',
    weight_multiplier: 1.5,
    max_score: 5,
    is_active: true,
  },
  {
    question_key: 'urgency_level',
    question_text: 'How urgent is automation for this workflow in the next quarter?',
    category: 'strategy',
    weight_multiplier: 1.5,
    max_score: 5,
    is_active: true,
  },
  {
    question_key: 'selected_workflow_count',
    question_text: 'How many priority workflows are being considered for this rollout?',
    category: 'strategy',
    weight_multiplier: 2,
    max_score: 5,
    is_active: true,
  },
];

const statusColor = (status) => {
  const map = {
    approved: 'green',
    rejected: 'red',
    pending_approval: 'yellow',
    completed: 'teal',
    shadow_logged: 'blue',
  };
  return map[status] || 'gray';
};

const COMMAND_CENTER_CACHE_PREFIX = 'wowrolla_admin_command_center_cache_v1';

const readCache = (key) => {
  try {
    const cached = JSON.parse(localStorage.getItem(key) || 'null');
    return cached && typeof cached === 'object' ? cached : null;
  } catch {
    return null;
  }
};

const writeCache = (key, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignore storage errors
  }
};

const AdminCommandCenterPage = () => {
  const [tenantId, setTenantId] = useState('tenant-demo');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingQuestionKey, setEditingQuestionKey] = useState('');
  const [questionSearch, setQuestionSearch] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  const [readinessQuestions, setReadinessQuestions] = useState([]);
  const [journey, setJourney] = useState(null);
  const [pilotFeed, setPilotFeed] = useState([]);
  const [affiliateOverview, setAffiliateOverview] = useState(null);

  const [questionForm, setQuestionForm] = useState({
    question_key: '',
    question_text: '',
    category: 'operations',
    weight_multiplier: 1,
    max_score: 5,
    is_active: true,
  });

  const loadAll = async () => {
    const cleanTenant = (tenantId || '').trim();
    if (!cleanTenant) {
      setError('Tenant ID is required.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    const cacheKey = `${COMMAND_CENTER_CACHE_PREFIX}:${cleanTenant}`;
    const cached = readCache(cacheKey);
    if (cached) {
      setReadinessQuestions(Array.isArray(cached.readinessQuestions) ? cached.readinessQuestions : []);
      setJourney(cached.journey || null);
      setPilotFeed(Array.isArray(cached.pilotFeed) ? cached.pilotFeed : []);
      setAffiliateOverview(cached.affiliateOverview || null);
      setLoading(false);
    }
    try {
      const [readinessRes, journeyRes, pilotRes, affiliateRes] = await Promise.all([
        adminApi.getReadinessQuestions(cleanTenant),
        adminApi.getJourneyMasterView({ tenant_id: cleanTenant, limit: 250 }),
        adminApi.getPilotShadowFeed({ tenant_id: cleanTenant, limit: 100 }),
        adminApi.getAffiliateRevenueOverview({ tenant_id: cleanTenant }),
      ]);

      const nextReadinessQuestions = readinessRes.data?.items || [];
      const nextJourney = journeyRes.data || null;
      const nextPilotFeed = pilotRes.data?.items || [];
      const nextAffiliateOverview = affiliateRes.data || null;
      setReadinessQuestions(nextReadinessQuestions);
      setJourney(nextJourney);
      setPilotFeed(nextPilotFeed);
      setAffiliateOverview(nextAffiliateOverview);
      writeCache(cacheKey, {
        readinessQuestions: nextReadinessQuestions,
        journey: nextJourney,
        pilotFeed: nextPilotFeed,
        affiliateOverview: nextAffiliateOverview,
        updatedAt: new Date().toISOString(),
      });
      setSuccess('Command center data loaded.');
    } catch (loadError) {
      setError(loadError?.response?.data?.detail || 'Failed to load command center data.');
    } finally {
      setLoading(false);
    }
  };

  const upsertQuestion = async () => {
    const cleanTenant = (tenantId || '').trim();
    if (!cleanTenant || !questionForm.question_key || !questionForm.question_text) {
      setError('Tenant, question key, and question text are required.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await adminApi.upsertReadinessQuestion({
        tenant_id: cleanTenant,
        ...questionForm,
      });
      await loadAll();
      setEditingQuestionKey('');
      setQuestionForm({
        question_key: '',
        question_text: '',
        category: 'operations',
        weight_multiplier: 1,
        max_score: 5,
        is_active: true,
      });
      setSuccess(editingQuestionKey ? 'Readiness question updated.' : 'Readiness question saved.');
    } catch (saveError) {
      setError(saveError?.response?.data?.detail || 'Failed to save readiness question.');
      setLoading(false);
    }
  };

  const loadQuestionIntoForm = (item) => {
    setEditingQuestionKey(String(item.question_key || ''));
    setQuestionForm({
      question_key: String(item.question_key || ''),
      question_text: String(item.question_text || ''),
      category: String(item.category || 'operations'),
      weight_multiplier: Number(item.weight_multiplier || 1),
      max_score: Number(item.max_score || 5),
      is_active: Boolean(item.is_active ?? true),
    });
  };

  const clearQuestionForm = () => {
    setEditingQuestionKey('');
    setQuestionForm({
      question_key: '',
      question_text: '',
      category: 'operations',
      weight_multiplier: 1,
      max_score: 5,
      is_active: true,
    });
  };

  const toggleQuestionActive = async (item) => {
    const cleanTenant = (tenantId || '').trim();
    if (!cleanTenant || !item?.question_key) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await adminApi.upsertReadinessQuestion({
        tenant_id: cleanTenant,
        question_key: String(item.question_key || ''),
        question_text: String(item.question_text || ''),
        category: String(item.category || 'operations'),
        weight_multiplier: Number(item.weight_multiplier || 1),
        max_score: Number(item.max_score || 5),
        is_active: !(item.is_active ?? true),
      });
      await loadAll();
      setSuccess('Question status updated.');
    } catch (toggleError) {
      setError(toggleError?.response?.data?.detail || 'Failed to toggle question status.');
      setLoading(false);
    }
  };

  const seedDefaultQuestions = async () => {
    const cleanTenant = (tenantId || '').trim();
    if (!cleanTenant) {
      setError('Tenant ID is required.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await Promise.all(
        DEFAULT_READINESS_QUESTIONS.map((question) => adminApi.upsertReadinessQuestion({
          tenant_id: cleanTenant,
          ...question,
        }))
      );
      await loadAll();
      setSuccess('Default readiness questions seeded (force upsert).');
    } catch (seedError) {
      setError(seedError?.response?.data?.detail || 'Failed to seed default readiness questions.');
      setLoading(false);
    }
  };

  const setApproval = async (runId, nextApproved) => {
    const cleanTenant = (tenantId || '').trim();
    if (!cleanTenant || !runId) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await adminApi.setPilotRunApproval(runId, {
        tenant_id: cleanTenant,
        is_approved: nextApproved,
      });
      await loadAll();
    } catch (approvalError) {
      setError(approvalError?.response?.data?.detail || 'Failed to update pilot approval.');
      setLoading(false);
    }
  };

  const stageCounts = useMemo(() => journey?.stage_counts || {}, [journey]);
  const existingQuestionKeys = useMemo(
    () => new Set((readinessQuestions || []).map((item) => String(item.question_key || ''))),
    [readinessQuestions]
  );
  const missingDefaults = useMemo(
    () => DEFAULT_READINESS_QUESTIONS.filter((item) => !existingQuestionKeys.has(item.question_key)),
    [existingQuestionKeys]
  );
  const filteredReadinessQuestions = useMemo(() => {
    const query = (questionSearch || '').trim().toLowerCase();
    return readinessQuestions.filter((item) => {
      if (showActiveOnly && !item.is_active) {
        return false;
      }
      if (!query) {
        return true;
      }
      const key = String(item.question_key || '').toLowerCase();
      const text = String(item.question_text || '').toLowerCase();
      const category = String(item.category || '').toLowerCase();
      return key.includes(query) || text.includes(query) || category.includes(query);
    });
  }, [readinessQuestions, questionSearch, showActiveOnly]);

  const readinessStats = useMemo(() => {
    const total = readinessQuestions.length;
    const active = readinessQuestions.filter((q) => q.is_active).length;
    const inactive = total - active;
    const defaultsPresent = DEFAULT_READINESS_QUESTIONS.filter((q) => existingQuestionKeys.has(q.question_key)).length;
    return { total, active, inactive, defaultsPresent };
  }, [readinessQuestions, existingQuestionKeys]);

  const cloneQuestionIntoForm = (item) => {
    const sourceKey = String(item.question_key || '');
    setEditingQuestionKey('');
    setQuestionForm({
      question_key: sourceKey ? `${sourceKey}_copy` : '',
      question_text: String(item.question_text || ''),
      category: String(item.category || 'operations'),
      weight_multiplier: Number(item.weight_multiplier || 1),
      max_score: Number(item.max_score || 5),
      is_active: Boolean(item.is_active ?? true),
    });
  };

  const seedMissingDefaultQuestions = async () => {
    const cleanTenant = (tenantId || '').trim();
    if (!cleanTenant) {
      setError('Tenant ID is required.');
      return;
    }
    if (missingDefaults.length === 0) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await Promise.all(
        missingDefaults.map((question) => adminApi.upsertReadinessQuestion({
          tenant_id: cleanTenant,
          ...question,
        }))
      );
      await loadAll();
      setSuccess('Missing default readiness questions seeded.');
    } catch (seedError) {
      setError(seedError?.response?.data?.detail || 'Failed to seed missing readiness questions.');
      setLoading(false);
    }
  };

  const inp = 'border border-gray-700 rounded px-3 py-2 bg-gray-900 text-white text-sm w-full focus:outline-none focus:border-gray-500';
  const th = 'text-left text-xs font-medium text-gray-400 uppercase tracking-wider py-2 px-3';
  const td = 'py-2 px-3 text-sm text-gray-200';

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Admin Command Center</h2>
          <p className="text-gray-400 text-sm">Readiness weights, journey progress, shadow pilot approvals, and affiliate overview</p>
        </div>
        <Btn variant="default" to="/admin/dashboard">Back to Admin Dashboard</Btn>
      </div>

      {/* Tenant loader */}
      <div className="border border-gray-700 rounded-lg p-6 space-y-3">
        <div className="flex items-end gap-3">
          <div className="w-80">
            <label className="block text-xs text-gray-400 mb-1">Tenant ID</label>
            <input className={inp} placeholder="tenant-demo" value={tenantId} onChange={(e) => setTenantId(e.target.value)} />
          </div>
          <Btn onClick={loadAll} loading={loading}>Load Command Center</Btn>
        </div>
        {error && <p className="text-red-400 text-sm">{error}</p>}
        {success && <p className="text-green-400 text-sm">{success}</p>}
      </div>

      {/* Row 1: Readiness + Journey */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Readiness Studio */}
        <div className="border border-gray-700 rounded-lg p-6 space-y-4">
          <h4 className="text-base font-semibold text-white">Readiness Studio Questions</h4>
          <div className="flex flex-wrap gap-2">
            <Bdg>Total: {readinessStats.total}</Bdg>
            <Bdg color="green">Active: {readinessStats.active}</Bdg>
            <Bdg color="gray">Inactive: {readinessStats.inactive}</Bdg>
            <Bdg color="blue">Defaults Present: {readinessStats.defaultsPresent}/9</Bdg>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Question Key</label>
              <input className={inp} value={questionForm.question_key} disabled={Boolean(editingQuestionKey)}
                onChange={(e) => setQuestionForm((p) => ({ ...p, question_key: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Category</label>
              <input className={inp} value={questionForm.category}
                onChange={(e) => setQuestionForm((p) => ({ ...p, category: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Question Text</label>
            <input className={inp} value={questionForm.question_text}
              onChange={(e) => setQuestionForm((p) => ({ ...p, question_text: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Weight Multiplier</label>
              <input className={inp} type="number" min="0" step="0.1" value={questionForm.weight_multiplier}
                onChange={(e) => setQuestionForm((p) => ({ ...p, weight_multiplier: Number(e.target.value || 0) }))} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Max Score</label>
              <input className={inp} type="number" min="1" step="1" value={questionForm.max_score}
                onChange={(e) => setQuestionForm((p) => ({ ...p, max_score: Number(e.target.value || 5) }))} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn onClick={upsertQuestion} loading={loading}>
              {editingQuestionKey ? 'Update Readiness Question' : 'Save Readiness Question'}
            </Btn>
            {editingQuestionKey && <Btn variant="default" onClick={clearQuestionForm} disabled={loading}>Cancel Edit</Btn>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn variant="default" onClick={seedMissingDefaultQuestions} loading={loading} disabled={missingDefaults.length === 0}>
              Seed Missing Defaults ({missingDefaults.length})
            </Btn>
            <Btn variant="light" onClick={seedDefaultQuestions} loading={loading}>Force Upsert 9 Defaults</Btn>
          </div>

          <hr className="border-gray-700" />
          <p className="text-xs text-gray-400">Default readiness set preview</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-700">{['Key','Category','Weight','Status'].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody>
                {DEFAULT_READINESS_QUESTIONS.map((item) => {
                  const exists = existingQuestionKeys.has(item.question_key);
                  return (
                    <tr key={`default-${item.question_key}`} className="border-b border-gray-800">
                      <td className={td}>{item.question_key}</td>
                      <td className={td}>{item.category}</td>
                      <td className={td}>{item.weight_multiplier}</td>
                      <td className={td}><Bdg color={exists ? 'green' : 'yellow'}>{exists ? 'present' : 'missing'}</Bdg></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Search Existing Questions</label>
            <input className={inp} placeholder="Search by key, text, or category" value={questionSearch}
              onChange={(e) => setQuestionSearch(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" checked={showActiveOnly} onChange={(e) => setShowActiveOnly(e.target.checked)} className="rounded" />
            Show active questions only
          </label>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-700">{['Key','Category','Weight','Max','Active','Actions'].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody>
                {filteredReadinessQuestions.slice(0, 10).map((item) => (
                  <tr key={item.question_key} className="border-b border-gray-800 hover:bg-gray-900/40">
                    <td className={td}>{item.question_key}</td>
                    <td className={td}>{item.category}</td>
                    <td className={td}>{item.weight_multiplier}</td>
                    <td className={td}>{item.max_score}</td>
                    <td className={td}><Bdg color={item.is_active ? 'green' : 'gray'}>{item.is_active ? 'active' : 'inactive'}</Bdg></td>
                    <td className={td}>
                      <div className="flex gap-1">
                        <Btn size="xs" variant="light" onClick={() => loadQuestionIntoForm(item)} disabled={loading}>Edit</Btn>
                        <Btn size="xs" variant="light" onClick={() => cloneQuestionIntoForm(item)} disabled={loading}>Clone</Btn>
                        <Btn size="xs" color={item.is_active ? 'gray' : 'green'} onClick={() => toggleQuestionActive(item)} loading={loading}>
                          {item.is_active ? 'Disable' : 'Enable'}
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredReadinessQuestions.length === 0 && (
                  <tr><td colSpan={6} className={`${td} text-gray-500`}>No readiness questions match current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Journey Master View */}
        <div className="border border-gray-700 rounded-lg p-6 space-y-4">
          <h4 className="text-base font-semibold text-white">Journey Master View</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stageCounts).map(([stage, count]) => (
              <Bdg key={stage}>{stage}: {count}</Bdg>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-700"><th className={th}>Client</th><th className={th}>Stages</th></tr></thead>
              <tbody>
                {(journey?.clients || []).slice(0, 10).map((item) => (
                  <tr key={`${item.tenant_id}-${item.client_id}`} className="border-b border-gray-800">
                    <td className={td}>{item.client_id}</td>
                    <td className={td}>{Object.keys(item.stages || {}).join(', ') || 'n/a'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Row 2: Pilot Feed + Affiliate */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 border border-gray-700 rounded-lg p-6 space-y-4">
          <h4 className="text-base font-semibold text-white">10-Run Pilot Feed</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-700">{['Run','Recipe','Status','Approval'].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody>
                {pilotFeed.slice(0, 15).map((run) => (
                  <tr key={run.run_id} className="border-b border-gray-800 hover:bg-gray-900/40">
                    <td className={td}>{run.run_id}</td>
                    <td className={td}>{run.recipe_key}</td>
                    <td className={td}><Bdg color={statusColor(run.status)}>{run.status}</Bdg></td>
                    <td className={td}>
                      <div className="flex gap-1">
                        <Btn size="xs" color="green" onClick={() => setApproval(run.run_id, true)} loading={loading}>Approve</Btn>
                        <Btn size="xs" color="red" onClick={() => setApproval(run.run_id, false)} loading={loading}>Reject</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
                {pilotFeed.length === 0 && (
                  <tr><td colSpan={4} className={`${td} text-gray-500`}>No pilot runs found for this tenant.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border border-gray-700 rounded-lg p-6 space-y-3">
          <h4 className="text-base font-semibold text-white">Affiliate Revenue</h4>
          <p className="text-xs text-gray-400">Projected Revenue</p>
          <p className="text-3xl font-bold text-white">${affiliateOverview?.projected_revenue || 0}</p>
          <p className="text-xs text-gray-400">Tracked Tools: {affiliateOverview?.total_tools || 0}</p>
          <div className="flex flex-col gap-1">
            {Object.entries(affiliateOverview?.status_counts || {}).map(([status, count]) => (
              <Bdg key={status}>{status}: {count}</Bdg>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCommandCenterPage;
